/* eslint-disable functional/no-let */
import { scrollInterval } from './config';
import { throttle } from './throttle';

const maxHeartbeat = 60000; // Cap to 60s

/**
 * Genertates the next time interval given the last one following the pattern:
 *  2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 25, 30, 40, 50,...
 * @param lastBeacon - The last beacon time
 * @returns The next time interval
 */
export const defaultNextBeacon = (lastBeacon: number) => {
  if (lastBeacon <= 18000) {
    return lastBeacon + 2000;
  } else if (lastBeacon <= 25000) {
    return lastBeacon + 5000;
  }
  return lastBeacon + 10000;
};

/**
 * Generate a function that will apply the threshold to see if the element is in the viewport
 */
const intersectionEntryInView =
  (threshold: number) => (entry: IntersectionObserverEntry) =>
    entry.intersectionRect.height >
    threshold * (entry.rootBounds?.height || window.innerHeight);

let hasBeenEngaged = false;

export const getHasBeenEngaged = () => hasBeenEngaged;

/**
 * Start tracking an element's time in view
 * NOTE: This will dispatch events from the element passed in
 * @param el - The element to track
 * @param intersectionObserver - Whether or not IntersectionObserver is available
 * @param engagementTimer - The time in view to consider the element engaged (optional: Default 8000)
 * @param intersectionThreshold - The threshold to consider the element in view (optional: Default 0.5)
 * @param calcNextBeacon - A function that takes the current beacon and returns the next beacon time
 * (optional: Default 2,4,6,8,10,15,20,25,30,40,50,...)
 * @returns A function that can be called to stop tracking the element
 */
export const trackHeartbeat = (
  el: Readonly<HTMLElement>,
  intersectionObserver: boolean,
  engagementTimer?: number,
  intersectionThreshold = 0.5,
  calcNextBeacon = defaultNextBeacon,
) => {
  let accumulatedTime = 0; // Accumulated time in view (in ms)
  let startTime = 0; // Time when the element last crossed the threshold
  let nextBeacon = 2000; // Time when the next beacon should be sent
  const evTimer = engagementTimer || 8000;
  let inView = false;
  let hasBeenViewed = false;
  let hasBeenImpressed = false;
  let timeoutId = 0;
  let observer: IntersectionObserver | null = null;

  const ioInView = intersectionEntryInView(intersectionThreshold);

  const fireBeacon = () => {
    const now = Date.now();
    if (!hasBeenEngaged && accumulatedTime >= evTimer) {
      hasBeenEngaged = true;
      el.dispatchEvent(new Event('flipp-engagement'));
    }
    if (document.hasFocus() && !document.hidden && nextBeacon <= maxHeartbeat) {
      accumulatedTime += now - startTime;
      el.dispatchEvent(
        new CustomEvent('flipp-heartbeat', { detail: { time: nextBeacon } }),
      );
      nextBeacon = calcNextBeacon(nextBeacon);
    }
    startTime = now;
    if (nextBeacon <= maxHeartbeat) {
      timeoutId = window.setTimeout(fireBeacon, nextBeacon - accumulatedTime);
    }
  };

  const leaveView = () => {
    inView = false;
    const elapsed = Date.now() - startTime;
    accumulatedTime += elapsed;
    startTime = 0;
    clearTimeout(timeoutId);
  };

  const enterView = () => {
    inView = true;
    startTime = Date.now();
    timeoutId = window.setTimeout(fireBeacon, nextBeacon - accumulatedTime);
    if (!hasBeenImpressed) {
      hasBeenImpressed = true;
      el.dispatchEvent(new Event('flipp-open'));
    }
  };

  const scrollHandler = throttle(() => {
    const rect = el.getBoundingClientRect();
    const isVisible = !(rect.top > window.innerHeight || rect.bottom < 0);
    const halfWindow = window.innerHeight * 0.5;
    const halfInView = rect.top < halfWindow && rect.bottom > halfWindow;

    if (!hasBeenViewed && isVisible) {
      hasBeenViewed = true;
      el.dispatchEvent(new Event('flipp-view'));
    }

    if (!inView && halfInView) {
      enterView();
    } else if (inView && !halfInView) {
      leaveView();
    }
    return null;
  }, scrollInterval);

  const intersectionHandler = (
    entries: readonly IntersectionObserverEntry[],
  ) => {
    if (!hasBeenViewed && entries.some(entry => entry.isIntersecting)) {
      hasBeenViewed = true;
      el.dispatchEvent(new Event('flipp-view'));
    }
    // If we weren't in view but at least one entry is in view, enter view
    if (!inView && entries.some(ioInView)) {
      enterView();

      // If we were in view but none of the entries are in view, leave view
    } else if (inView && entries.every(e => !ioInView(e))) {
      leaveView();
    }
  };

  if (intersectionObserver) {
    // Since intersection observer uses the % of the element in view as it's threshold we need to
    // calculate the % of the element that would equate to half the viewport height and track that
    const halfScreenPercentage = window.innerHeight / 2 / el.clientHeight;

    // Guard against edge cases where the element is smaller than the viewport, etc.
    const step = Math.min(1, Math.max(0.001, halfScreenPercentage));

    // Track the halfway point plus points 10% either side (and 0%, 100%) for added safety
    // Threshold must be between 0 and 1
    const threshold = [0, step * 0.9, step, Math.min(1, step * 1.1), 1];

    observer = new IntersectionObserver(intersectionHandler, {
      threshold,
      rootMargin: `0px 1000px 0px 1000px`, // Ignore width as a factor
      root: null,
    });
    observer.observe(el);
  } else {
    window.addEventListener('scroll', scrollHandler);
  }

  // Return a function that can be called to stop tracking the element
  return () => {
    window.clearTimeout(timeoutId);
    if (observer) {
      observer.disconnect();
    }
    window.removeEventListener('scroll', scrollHandler);

    // Return the tombstone time
    return accumulatedTime + Date.now() - startTime;
  };
};
